import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { CartItem, Product, CreateCartRequest } from '../types/Product';
import { createCart, getUserCarts, getProduct } from '../lib/api';
import { useAuth } from './AuthContext';

interface CartContextType {
  cartItems: CartItem[];
  cartCount: number;
  cartTotal: number;
  addToCart: (product: Product, quantity?: number) => Promise<void>;
  removeFromCart: (productId: number) => void;
  updateQuantity: (productId: number, quantity: number) => void;
  clearCart: () => void;
  isLoading: boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

interface CartProviderProps {
  children: ReactNode;
}

export function CartProvider({ children }: CartProviderProps) {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user, isAuthenticated } = useAuth();

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cart_items');
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
      }
    }
  }, []);

  // Save cart to localStorage whenever cartItems changes
  useEffect(() => {
    localStorage.setItem('cart_items', JSON.stringify(cartItems));
  }, [cartItems]);

  const syncLocalCartToAPI = useCallback(async () => {
    if (!user || cartItems.length === 0) return;

    try {
      const cartData: CreateCartRequest = {
        userId: user.id,
        date: new Date().toISOString(),
        products: cartItems.map((item) => ({
          productId: item.product.id,
          quantity: item.quantity,
        })),
      };

      await createCart(cartData);
    } catch (error) {
      console.error('Error syncing cart to API:', error);
    }
  }, [user, cartItems]);

  const syncCartWithAPI = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      // Get user's carts from API
      const userCarts = await getUserCarts(user.id);

      // For demo purposes, we'll use the most recent cart or create a new one
      if (userCarts.length > 0 && cartItems.length === 0) {
        const latestCart = userCarts[userCarts.length - 1];
        const cartItemsFromAPI: CartItem[] = [];

        // Fetch product details for each cart item
        for (const cartProduct of latestCart.products) {
          try {
            const product = await getProduct(cartProduct.productId);
            cartItemsFromAPI.push({
              product,
              quantity: cartProduct.quantity,
            });
          } catch (error) {
            console.error(`Error fetching product ${cartProduct.productId}:`, error);
          }
        }

        setCartItems(cartItemsFromAPI);
      } else if (cartItems.length > 0) {
        // Sync local cart to API
        await syncLocalCartToAPI();
      }
    } catch (error) {
      console.error('Error syncing cart with API:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user, cartItems, syncLocalCartToAPI]);

  // Sync with API when user logs in/out
  useEffect(() => {
    if (isAuthenticated && user) {
      syncCartWithAPI();
    } else if (!isAuthenticated) {
      // Keep local cart when logged out
    }
  }, [isAuthenticated, user, syncCartWithAPI]);

  const addToCart = async (product: Product, quantity = 1) => {
    setCartItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.product.id === product.id);

      if (existingItem) {
        return prevItems.map((item) =>
          item.product.id === product.id ? { ...item, quantity: item.quantity + quantity } : item
        );
      } else {
        return [...prevItems, { product, quantity }];
      }
    });

    // Sync with API if user is logged in
    if (isAuthenticated && user) {
      setTimeout(syncLocalCartToAPI, 500); // Debounce API calls
    }
  };

  const removeFromCart = (productId: number) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.product.id !== productId));
  };

  const updateQuantity = (productId: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }

    setCartItems((prevItems) =>
      prevItems.map((item) => (item.product.id === productId ? { ...item, quantity } : item))
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cartItems.reduce((sum, item) => sum + item.product.price * item.quantity, 0);

  const value: CartContextType = {
    cartItems,
    cartCount,
    cartTotal,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    isLoading,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
